import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { 
  Quote, 
  Clock, 
  Save, 
  Plus, 
  Trash2, 
  Info, 
  CheckCircle2 
} from 'lucide-react';
import './FormsUnified.css';

const DEFAULT_FORM_DATA = {
  kicker: 'Testimonials',
  title: 'Stories that shape our community',
  description: 'Parents, students, and teachers share the moments that matter most.',
  submit_tip_title: 'Submit Testimonial',
  submit_tip_subtitle: 'Help families discover Permata Harapan',
  submit_tips: [
    'Share one memorable moment from learning.',
    'Mention a teacher, program, or activity.',
    'Upload a smiling photo for the card.'
  ]
};

export default function ContactTestimonialsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const getAuthHeaders = useCallback((includeContentType = true) => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (includeContentType) headers['Content-Type'] = 'application/json';
    return headers;
  }, []);

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    submit_tip_title: false,
    submit_tip_subtitle: false,
    submit_tips: false
  });
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=contact-us`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'contact-testimonials' || s.section_type === 'testimonials');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=contact-us`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'contact-testimonials' || s.section_type === 'testimonials');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        const rawContent = foundSection.content_published || foundSection.content;
        let parsed = rawContent;
        if (typeof rawContent === 'string') {
          try {
            parsed = JSON.parse(rawContent);
          } catch (e) {
            console.error('Failed to parse contact-testimonials content', e);
          }
        }

        const dataEn = parsed?.en || parsed || {};
        const newFormData = {
          kicker: dataEn.kicker || 'Testimonials',
          title: dataEn.title || 'Stories that shape our community',
          description: dataEn.description || 'Parents, students, and teachers share the moments that matter most.',
          submit_tip_title: dataEn.submit_tip_title || 'Submit Testimonial',
          submit_tip_subtitle: dataEn.submit_tip_subtitle || 'Help families discover Permata Harapan',
          submit_tips: Array.isArray(dataEn.submit_tips) && dataEn.submit_tips.length > 0 ? dataEn.submit_tips : [
            'Share one memorable moment from learning.',
            'Mention a teacher, program, or activity.',
            'Upload a smiling photo for the card.'
          ]
        };

        setFormData(newFormData);
        setOriginalData(newFormData);

        // Fetch pending changes
        if (foundSection.id) {
          try {
            const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
              credentials: 'include',
              headers: getAuthHeaders()
            });
            if (pendRes.ok) {
              const pendJson = await pendRes.json();
              if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
                const pendMap = { 
                  kicker: false, 
                  title: false, 
                  description: false,
                  submit_tip_title: false,
                  submit_tip_subtitle: false,
                  submit_tips: false
                };
                pendJson.data.pending_changes.forEach(change => {
                  if (change.status === 'pending') {
                    if (pendMap[change.field_id] !== undefined) {
                      pendMap[change.field_id] = true;
                    }
                  }
                });
                setPendingFields(pendMap);
              }
            }
          } catch (err) {
            console.warn('Could not load pending changes for contact-testimonials', err);
          }
        }
      }
    } catch (err) {
      console.error('Error loading contact-testimonials section:', err);
      toast.error('Failed to load contact testimonials section data');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTipChange = (index, value) => {
    setFormData(prev => {
      const newTips = [...prev.submit_tips];
      newTips[index] = value;
      return { ...prev, submit_tips: newTips };
    });
  };

  const handleAddTip = () => {
    setFormData(prev => ({
      ...prev,
      submit_tips: [...prev.submit_tips, '']
    }));
  };

  const handleRemoveTip = (index) => {
    setFormData(prev => ({
      ...prev,
      submit_tips: prev.submit_tips.filter((_, idx) => idx !== index)
    }));
  };

  const handleSubmitField = async (fieldId, proposedValue, currentVal, customName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan di database');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const payload = {
        section_id: sectionId,
        field_id: fieldId,
        field_name: customName || (fieldId.charAt(0).toUpperCase() + fieldId.slice(1)),
        old_value: currentVal !== undefined ? currentVal : originalData[fieldId],
        new_value: proposedValue !== undefined ? proposedValue : formData[fieldId],
        locale: 'en'
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && (json.success || json.ok)) {
        toast.success(`"${customName || fieldId}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        setOriginalData(prev => ({ ...prev, [fieldId]: payload.new_value }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        toast.error(json.message || json.error || 'Gagal mengajukan perubahan');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Terjadi kesalahan saat menyimpan perubahan');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Memuat pengaturan Testimonials...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />
      
      {/* STANDARD UNIFIED HEADER */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Quote size={22} />
        </div>
        <div>
          <h2 className="unified-title">Testimonials Section Settings</h2>
          <p className="unified-subtitle">
            Atur judul, kicker, dan panduan formulir testimoni publik. Data ulasan pengunjung dikelola melalui menu Testimonials terpisah.
          </p>
        </div>
        {sectionId && (
          <span className="unified-section-badge">ID: {sectionId}</span>
        )}
      </div>

      {/* INFORMATIONAL CALLOUT BANNER */}
      <div style={{
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        border: '1px solid #a7f3d0',
        borderRadius: '12px',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        marginBottom: '22px'
      }}>
        <Info size={22} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '13.5px', color: '#065f46', lineHeight: '1.6' }}>
          <strong style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#047857' }}>
            Moderasi Testimoni Pengguna Dikelola Terpisah
          </strong>
          Ulasan yang dikirimkan oleh pengguna (foto, nama, role, dan pesan testimoni) tidak diedit manual di CMS ini. Anda dapat meninjau kiriman ulasan baru, mengaktifkan/menonaktifkan tombol status publikasi (ON / OFF), serta menghapus ulasan yang tidak layak melalui menu khusus <strong>Testimonials</strong> di bilah navigasi utama sidebar.
        </div>
      </div>

      <div className="unified-form-body">
        {/* GROUP 1: SECTION HEADERS */}
        <div className="unified-grid-2">
          {/* Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Kicker</label>
              {pendingFields.kicker && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleInputChange('kicker', e.target.value)}
                disabled={pendingFields.kicker}
                placeholder="e.g. Testimonials"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('kicker', formData.kicker, originalData.kicker, 'Section Kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Title</label>
              {pendingFields.title && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                disabled={pendingFields.title}
                placeholder="e.g. Stories that shape our community"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('title', formData.title, originalData.title, 'Section Title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              >
                <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* Section Description */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Section Description</label>
              <p className="unified-hint">Deskripsi ringkas yang tampil di bawah judul testimoni.</p>
            </div>
            {pendingFields.description && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-column">
            <textarea
              className="unified-textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={pendingFields.description}
              placeholder="e.g. Parents, students, and teachers share the moments that matter most."
            />
            <div className="unified-align-right">
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('description', formData.description, originalData.description, 'Section Description')}
                disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              >
                <Save size={14} /> {submitting.description ? 'Saving...' : (pendingFields.description ? 'Pending' : 'Save Description')}
              </button>
            </div>
          </div>
        </div>

        {/* GROUP 2: PUBLIC FORM GUIDANCE / SUBMISSION TIPS */}
        <div className="unified-form-section-divider" style={{ marginTop: '24px' }}>
          <CheckCircle2 size={16} />
          <span>Panduan Pengiriman Testimoni Publik (Submission Box)</span>
        </div>

        <div className="unified-grid-2">
          {/* Submit Tip Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Form Box Badge Kicker</label>
              {pendingFields.submit_tip_title && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.submit_tip_title}
                onChange={(e) => handleInputChange('submit_tip_title', e.target.value)}
                disabled={pendingFields.submit_tip_title}
                placeholder="e.g. Submit Testimonial"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('submit_tip_title', formData.submit_tip_title, originalData.submit_tip_title, 'Form Box Badge Kicker')}
                disabled={submitting.submit_tip_title || pendingFields.submit_tip_title || formData.submit_tip_title === originalData.submit_tip_title}
              >
                <Save size={14} /> {submitting.submit_tip_title ? 'Saving...' : (pendingFields.submit_tip_title ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* Submit Tip Subtitle */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Form Box Main Title</label>
              {pendingFields.submit_tip_subtitle && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.submit_tip_subtitle}
                onChange={(e) => handleInputChange('submit_tip_subtitle', e.target.value)}
                disabled={pendingFields.submit_tip_subtitle}
                placeholder="e.g. Help families discover Permata Harapan"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('submit_tip_subtitle', formData.submit_tip_subtitle, originalData.submit_tip_subtitle, 'Form Box Main Title')}
                disabled={submitting.submit_tip_subtitle || pendingFields.submit_tip_subtitle || formData.submit_tip_subtitle === originalData.submit_tip_subtitle}
              >
                <Save size={14} /> {submitting.submit_tip_subtitle ? 'Saving...' : (pendingFields.submit_tip_subtitle ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* Dynamic Submission Tips List */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Poin-Poin Panduan ({formData.submit_tips.length} Tips)</label>
              <p className="unified-hint">
                Tips ringkas yang tampil di samping form untuk memandu orang tua/siswa saat menulis ulasan.
              </p>
            </div>
            <button
              type="button"
              className="unified-secondary-btn"
              onClick={handleAddTip}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={15} /> Tambah Tips
            </button>
          </div>

          <div className="unified-sub-list">
            {formData.submit_tips.map((tip, idx) => (
              <div key={idx} className="unified-list-item-row">
                <span style={{ 
                  background: '#f1f5f9', 
                  color: '#475569', 
                  fontWeight: 700, 
                  fontSize: '12px',
                  width: '26px', 
                  height: '26px', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {idx + 1}
                </span>
                <input
                  type="text"
                  className="unified-input"
                  value={tip}
                  onChange={(e) => handleTipChange(idx, e.target.value)}
                  placeholder={`Tips #${idx + 1}`}
                />
                <button
                  type="button"
                  className="unified-danger-icon-btn"
                  onClick={() => handleRemoveTip(idx)}
                  title="Hapus Tips"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="unified-multi-action-bar">
            <span style={{ fontSize: '12.5px', color: '#64748b' }}>
              Pastikan panduan singkat dan jelas untuk mempermudah responden mengisi cerita mereka.
            </span>
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('submit_tips', formData.submit_tips, originalData.submit_tips, 'Panduan Testimoni')}
              disabled={submitting.submit_tips || pendingFields.submit_tips || JSON.stringify(formData.submit_tips) === JSON.stringify(originalData.submit_tips)}
            >
              <Save size={14} /> {submitting.submit_tips ? 'Saving...' : (pendingFields.submit_tips ? 'Pending' : 'Save All Tips')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
